import { CheckingRoleToggle } from "./CheckingRoleToggle";

/**
 * The standing warning on a dial that is only being tried out (KEY-283).
 *
 * A checking dial is a real instance — statuses, files and notes all work — which is exactly why it needs
 * saying plainly that none of it is kept. When the version leaves checking, in either direction (back to
 * draft for another edit, or on to publication), this dial and everything entered into it are deleted.
 *
 * Deliberately NOT dismissible, unlike UpgradeNotice: dismissing it would leave someone entering real work
 * into a dial that is about to disappear, which is the one outcome this exists to prevent.
 *
 * KEY-524: also shown on the CHILDREN of a group under test, where the reason differs and the copy has to
 * follow. A child is not itself out for checking — it runs its own latest version, which is usually
 * published — so "this runs an unpublished version" would be a plain untruth there. What is true of both
 * is the part that matters: the dial is thrown away when testing ends.
 */
export function TestingNotice({
  simulatedRole,
  onSimulatedRoleChange,
  simulationBusy,
  viaGroup = false,
}: {
  /** KEY-498: the role the server says it evaluated as, or null. Omit to hide the toggle entirely. */
  simulatedRole?: string | null;
  onSimulatedRoleChange?: (role: string | null) => void;
  simulationBusy?: boolean;
  /** True on a child of a group under test — this dial's own version is not the one being checked. */
  viaGroup?: boolean;
} = {}) {
  const accent = "var(--st-risk, #7a3b9c)";
  return (
    <div
      className="kd-card"
      style={{
        padding: "var(--pad-card, 16px)",
        margin: "0 0 14px",
        borderLeft: `3px solid ${accent}`,
      }}
      role="region"
      aria-label="Testing dial notice"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
            color: "#fff", background: accent, borderRadius: 999, padding: "2px 9px",
          }}
        >
          Testing
        </span>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)" }}>
          {viaGroup
            ? "This dial is part of a group being tested, not used for real"
            : "This dial is being tested, not used for real"}
        </h3>
      </div>

      <p style={{ margin: "8px 0 0", color: "var(--text-muted)", fontSize: 13.5, lineHeight: 1.55 }}>
        {viaGroup
          ? "It was created alongside the group being tried out, so it's a throwaway copy rather than your real dial — go ahead and set statuses, upload files and leave notes to see how it behaves."
          : "It runs a version of the dial that hasn't been published yet, so it's here to be tried out — go ahead and set statuses, upload files and leave notes to see how it behaves."}
      </p>
      <p style={{ margin: "6px 0 0", color: "var(--text)", fontSize: 13.5, fontWeight: 600, lineHeight: 1.55 }}>
        Everything you enter here will be deleted when testing finishes. Don't record anything you need to
        keep.
      </p>

      {/* KEY-498. Hosted in the banner because it belongs to checking and nowhere else — this is the one
          place the dial's role rules can be tried out, since checking otherwise makes every checker an
          Admin, and Admin passes every gate. */}
      {onSimulatedRoleChange && (
        <CheckingRoleToggle
          simulatedRole={simulatedRole ?? null}
          onChange={onSimulatedRoleChange}
          busy={simulationBusy}
        />
      )}
    </div>
  );
}
